import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import { 
  BarChart3, 
  Package, 
  Users, 
  FileText, 
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  CreditCard,
  DollarSign,
  Bell,
  Settings,
  Search,
  Calendar,
  ChevronDown,
  ChevronRight,
  Menu,
  Activity,
  PlusCircle,
  User,
  LogOut,
  RefreshCw,
  Filter,
  PanelLeftClose,
  PanelLeftOpen,
  Moon,
  Sun,
  ArrowUpRight, 
  ArrowDownRight,
  Clock,
  X,
  Plus,
  Calculator,
  Receipt,
  ClipboardList,
  Eye,
  Edit,
  Trash2,
  Download,
  Send,
  CheckCircle,
  AlertCircle,
  Info,
  Loader2,
  Tag,
  Box,
  Package2,
  Layers,
  Folder
} from 'lucide-react';
import toast from 'react-hot-toast';

const Categories = () => {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Category state management
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryStats, setCategoryStats] = useState({});
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 12,
    total: 0,
    lastPage: 1
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    sort_by: 'main_cat',
    sort_order: 'asc'
  });

  // Add Category Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [addCategoryLoading, setAddCategoryLoading] = useState(false);
  const [addCategoryForm, setAddCategoryForm] = useState({
    main_cat: '',
    sub_cat: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // View Category Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Edit Category Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCategoryLoading, setEditCategoryLoading] = useState(false);
  const [editCategoryForm, setEditCategoryForm] = useState({
    main_cat: '',
    sub_cat: ''
  });
  const [editFormErrors, setEditFormErrors] = useState({});

  // Delete Category Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCategoryLoading, setDeleteCategoryLoading] = useState(false);


  const handleCardClick = useCallback((href) => {
    navigate(href);
  }, [navigate]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, []);

  // Add Category Modal handlers
  const handleAddCategoryFormChange = useCallback((field, value) => {
    setAddCategoryForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [formErrors]);

  const validateAddCategoryForm = useCallback(() => {
    const errors = {};
    
    if (!addCategoryForm.main_cat.trim()) {
      errors.main_cat = 'Main category is required';
    }
    
    if (!addCategoryForm.sub_cat.trim()) {
      errors.sub_cat = 'Sub category is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [addCategoryForm]);

  const handleAddCategory = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateAddCategoryForm()) {
      return;
    }
    
    setAddCategoryLoading(true);
    try {
      await apiService.createCategory(addCategoryForm);
      toast.success('Category added successfully!');
      setShowAddModal(false);
      setAddCategoryForm({
        main_cat: '',
        sub_cat: ''
      });
      setFormErrors({});
      
      // Refresh the categories list
      if (refreshCategories) {
        refreshCategories();
      }
      
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Failed to add category. Please try again.');
    } finally {
      setAddCategoryLoading(false);
    }
  }, [addCategoryForm, validateAddCategoryForm]);

  const handleCloseAddModal = useCallback(() => {
    setShowAddModal(false);
    setAddCategoryForm({
      main_cat: '',
      sub_cat: ''
    });
    setFormErrors({});
  }, []);

  // View Category handlers
  const handleViewCategory = useCallback((category) => {
    setSelectedCategory(category);
    setShowViewModal(true);
  }, []);

  const handleCloseViewModal = useCallback(() => {
    setShowViewModal(false);
    setSelectedCategory(null);
  }, []);

  // Edit Category handlers
  const handleEditCategory = useCallback((category) => {
    setSelectedCategory(category);
    setEditCategoryForm({
      main_cat: category.main_cat,
      sub_cat: category.sub_cat
    });
    setEditFormErrors({});
    setShowEditModal(true);
  }, []);

  const handleEditCategoryFormChange = useCallback((field, value) => {
    setEditCategoryForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (editFormErrors[field]) {
      setEditFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [editFormErrors]);

  const validateEditCategoryForm = useCallback(() => {
    const errors = {};
    
    if (!editCategoryForm.main_cat.trim()) {
      errors.main_cat = 'Main category is required';
    }
    
    if (!editCategoryForm.sub_cat.trim()) {
      errors.sub_cat = 'Sub category is required';
    }
    
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [editCategoryForm]);

  const handleUpdateCategory = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateEditCategoryForm()) {
      return;
    }
    
    setEditCategoryLoading(true);
    try {
      await apiService.updateCategory(selectedCategory.id, editCategoryForm);
      toast.success('Category updated successfully!');
      setShowEditModal(false);
      setSelectedCategory(null);
      setEditCategoryForm({
        main_cat: '',
        sub_cat: ''
      });
      setEditFormErrors({});
      
      // Refresh the categories list
      if (refreshCategories) {
        refreshCategories();
      }
      
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category. Please try again.');
    } finally {
      setEditCategoryLoading(false);
    }
  }, [editCategoryForm, validateEditCategoryForm, selectedCategory]);

  const handleCloseEditModal = useCallback(() => {
    setShowEditModal(false);
    setSelectedCategory(null);
    setEditCategoryForm({
      main_cat: '',
      sub_cat: ''
    });
    setEditFormErrors({});
  }, []);

  // Delete Category handlers
  const handleDeleteCategory = useCallback((category) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedCategory) return;
    
    setDeleteCategoryLoading(true);
    try {
      await apiService.deleteCategory(selectedCategory.id);
      toast.success('Category deleted successfully!');
      setShowDeleteModal(false);
      setSelectedCategory(null);
      
      // Refresh the categories list
      if (refreshCategories) {
        refreshCategories();
      }
      
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category. Please try again.');
    } finally {
      setDeleteCategoryLoading(false);
    }
  }, [selectedCategory]);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setSelectedCategory(null);
  }, []);

  // Create a refresh function that will be set after fetchCategories is defined
  const [refreshCategories, setRefreshCategories] = useState(null);

  // Fetch real data from API
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getCategories({
        search: filters.search,
        status: filters.status,
        page: pagination.currentPage,
        per_page: pagination.itemsPerPage,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order
      });
      
      // Handle the pagination response format
      const categoriesData = response.data || [];
      
      setCategories(categoriesData);
      
      // Update pagination info
      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          lastPage: response.pagination.last_page,
          currentPage: response.pagination.current_page
        }));
      }
      
    } catch (error) {
      console.error('Error fetching categories:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      
      // Try fallback to public endpoint
      console.log('Trying fallback to public categories endpoint...');
      try {
        const fallbackResponse = await apiService.getAllCategoriesPublic();
        
        const fallbackData = fallbackResponse.data || fallbackResponse;
        setCategories(Array.isArray(fallbackData) ? fallbackData : []);
        
        toast.success('Categories loaded successfully!');
        
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        toast.error(`Failed to fetch categories: ${error.message}`);
        
        // Final fallback to empty state
        setCategories([]);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.itemsPerPage]);

  // Fetch category statistics
  const fetchCategoryStats = useCallback(async () => {
    console.log('Starting to fetch category stats...');
    try {
      const response = await apiService.getCategoryStats();
      console.log('Category stats response:', response);
      
      if (response.data) {
        setCategoryStats(response.data);
      } else {
        console.log('No data in response:', response);
      }
    } catch (error) {
      console.error('Error fetching category stats:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      
      // Try fallback to public endpoint
      try {
        const fallbackResponse = await apiService.getCategoryStatsPublic();
        
        if (fallbackResponse.data) {
          setCategoryStats(fallbackResponse.data);
        }
      } catch (fallbackError) {
        console.error('Fallback stats also failed:', fallbackError);
        
        // Set default stats
        const defaultStats = {
          total_categories: 0,
          active_categories: 0,
          total_products: 0,
          average_products_per_category: 0
        };
        setCategoryStats(defaultStats);
      }
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchCategoryStats();
  }, [fetchCategoryStats]);

  // Set the refresh function after fetchCategories is defined
  useEffect(() => {
    setRefreshCategories(() => fetchCategories);
  }, [fetchCategories]);


  const getCategoryColor = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      purple: 'bg-purple-100 text-purple-800',
      orange: 'bg-orange-100 text-orange-800',
      red: 'bg-red-100 text-red-800',
      yellow: 'bg-yellow-100 text-yellow-800'
    };
    return colors[color] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Layout>
      <main className="flex-1 p-6 overflow-y-auto pt-20">
            <div className="max-w-7xl mx-auto space-y-8">
              {/* Page Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Category Management</h1>
                  <p className="text-gray-600">Organize your products with categories</p>
                </div>
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => {
                      if (refreshCategories) {
                        refreshCategories();
                      }
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
                  >
                    <RefreshCw size={18} />
                    Refresh
                  </button>
                  <button 
                    onClick={() => setShowAddModal(true)}
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center gap-2"
                  >
                    <Plus size={20} />
                    Add Category
                  </button>
                </div>
              </div>

              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="group bg-gradient-to-br from-blue-50 via-blue-100 to-blue-200 rounded-2xl p-6 border border-blue-200/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <Folder className="h-6 w-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Total</p>
                        <p className="text-xs text-blue-500">Categories</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-3xl font-bold text-blue-900">
                        {loading ? <Loader2 className="animate-spin" size={28} /> : categoryStats.total_categories || 0}
                      </p>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <p className="text-sm text-blue-700">All categories in system</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="group bg-gradient-to-br from-green-50 via-green-100 to-green-200 rounded-2xl p-6 border border-green-200/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-400/10 to-green-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <CheckCircle className="h-6 w-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-medium text-green-600 uppercase tracking-wide">Active</p>
                        <p className="text-xs text-green-500">Categories</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-3xl font-bold text-green-900">
                        {loading ? <Loader2 className="animate-spin" size={28} /> : categoryStats.active_categories || 0}
                      </p>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <p className="text-sm text-green-700">With products</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="group bg-gradient-to-br from-purple-50 via-purple-100 to-purple-200 rounded-2xl p-6 border border-purple-200/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-400/10 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <Package className="h-6 w-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Total</p>
                        <p className="text-xs text-purple-500">Products</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-3xl font-bold text-purple-900">
                        {loading ? <Loader2 className="animate-spin" size={28} /> : categoryStats.total_products || 0}
                      </p>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        <p className="text-sm text-purple-700">Across all categories</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="group bg-gradient-to-br from-orange-50 via-orange-100 to-orange-200 rounded-2xl p-6 border border-orange-200/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-400/10 to-orange-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <BarChart3 className="h-6 w-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-medium text-orange-600 uppercase tracking-wide">Average</p>
                        <p className="text-xs text-orange-500">Per Category</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-3xl font-bold text-orange-900">
                        {loading ? <Loader2 className="animate-spin" size={28} /> : categoryStats.average_products_per_category || 0}
                      </p>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <p className="text-sm text-orange-700">Products per category</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filters and Search */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                      <Filter className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Filters & Search</h3>
                      <p className="text-sm text-gray-500">Find and organize your categories</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 font-medium">Current filter:</span>
                    <span className={`px-3 py-1.5 text-xs font-bold rounded-full shadow-sm ${
                      filters.status === 'all' ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300' :
                      filters.status === 'active' ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300' :
                      'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 border border-gray-300'
                    }`}>
                      {filters.status === 'all' ? 'All Categories' :
                       filters.status === 'active' ? 'Active Only' :
                       'Inactive Only'}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="relative group">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" size={18} />
                    <input 
                      type="text" 
                      placeholder="Search categories..." 
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                    />
                  </div>
                  <div className="relative">
                    <select 
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white appearance-none cursor-pointer"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active (with products)</option>
                      <option value="inactive">Inactive (no products)</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                      if (refreshCategories) {
                        refreshCategories();
                      }
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  >
                    <Filter size={16} />
                    Apply Filters
                  </button>
                </div>
              </div>

              {/* Categories Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {loading ? (
                  Array.from({ length: 8 }).map((_, index) => (
                    <div key={index} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse shadow-sm">
                      <div className="flex items-center justify-between mb-6">
                        <div className="w-14 h-14 bg-gray-200 rounded-2xl"></div>
                        <div className="w-20 h-6 bg-gray-200 rounded-full"></div>
                      </div>
                      <div className="space-y-3">
                        <div className="w-4/5 h-5 bg-gray-200 rounded"></div>
                        <div className="w-full h-4 bg-gray-200 rounded"></div>
                        <div className="w-3/5 h-4 bg-gray-200 rounded"></div>
                      </div>
                      <div className="mt-6 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between">
                          <div className="w-24 h-4 bg-gray-200 rounded"></div>
                          <div className="flex space-x-2">
                            <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                            <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                            <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : categories.length === 0 ? (
                  <div className="col-span-full flex flex-col items-center justify-center py-16">
                    <div className="relative">
                      <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-6">
                        <Folder className="h-12 w-12 text-gray-400" />
                      </div>
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center">
                        <X className="h-4 w-4 text-red-500" />
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No categories found</h3>
                    <p className="text-gray-500 text-center max-w-md">
                      {filters.status === 'active' 
                        ? "No active categories found. Categories with products will appear here."
                        : filters.status === 'inactive'
                        ? "No inactive categories found. Categories without products will appear here."
                        : "Create your first category to get started organizing your products."
                      }
                    </p>
                    {filters.status === 'all' && (
                      <button 
                        onClick={() => setShowAddModal(true)}
                        className="mt-4 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl"
                      >
                        <Plus size={20} />
                        Create First Category
                      </button>
                    )}
                  </div>
                ) : (
                  categories.map((category, index) => {
                    const isActive = (category.product_count || 0) > 0;
                    const categoryColors = [
                      { bg: 'from-blue-50 via-blue-100 to-blue-200', border: 'border-blue-200/50', icon: 'from-blue-500 to-blue-600', accent: 'from-blue-400/10 to-blue-600/10' },
                      { bg: 'from-green-50 via-green-100 to-green-200', border: 'border-green-200/50', icon: 'from-green-500 to-green-600', accent: 'from-green-400/10 to-green-600/10' },
                      { bg: 'from-purple-50 via-purple-100 to-purple-200', border: 'border-purple-200/50', icon: 'from-purple-500 to-purple-600', accent: 'from-purple-400/10 to-purple-600/10' },
                      { bg: 'from-orange-50 via-orange-100 to-orange-200', border: 'border-orange-200/50', icon: 'from-orange-500 to-orange-600', accent: 'from-orange-400/10 to-orange-600/10' },
                      { bg: 'from-pink-50 via-pink-100 to-pink-200', border: 'border-pink-200/50', icon: 'from-pink-500 to-pink-600', accent: 'from-pink-400/10 to-pink-600/10' },
                      { bg: 'from-indigo-50 via-indigo-100 to-indigo-200', border: 'border-indigo-200/50', icon: 'from-indigo-500 to-indigo-600', accent: 'from-indigo-400/10 to-indigo-600/10' },
                      { bg: 'from-teal-50 via-teal-100 to-teal-200', border: 'border-teal-200/50', icon: 'from-teal-500 to-teal-600', accent: 'from-teal-400/10 to-teal-600/10' },
                      { bg: 'from-red-50 via-red-100 to-red-200', border: 'border-red-200/50', icon: 'from-red-500 to-red-600', accent: 'from-red-400/10 to-red-600/10' }
                    ];
                    const colorScheme = categoryColors[index % categoryColors.length];
                    
                    return (
                      <div 
                        key={category.id} 
                        className={`group bg-gradient-to-br ${colorScheme.bg} rounded-2xl border ${colorScheme.border} p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden`}
                      >
                        {/* Background Gradient Overlay */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${colorScheme.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                        
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4 relative z-10">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${colorScheme.icon} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                            <Folder className="h-6 w-6 text-white" />
                          </div>
                          <span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full shadow-sm ${
                            isActive 
                              ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300' 
                              : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 border border-gray-300'
                          }`}>
                            {isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        
                        {/* Content */}
                        <div className="space-y-3 relative z-10">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 group-hover:text-gray-800 transition-colors duration-300 mb-1">
                              {category.main_cat}
                            </h3>
                            <p className="text-sm text-gray-600 leading-relaxed">
                              {category.sub_cat}
                            </p>
                          </div>
                          
                          {/* Product Count */}
                          <div className="flex items-center space-x-2">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              isActive 
                                ? 'bg-gradient-to-br from-green-100 to-green-200 shadow-sm' 
                                : 'bg-gradient-to-br from-gray-100 to-gray-200 shadow-sm'
                            }`}>
                              <Package className={`h-4 w-4 ${
                                isActive ? 'text-green-600' : 'text-gray-500'
                              }`} />
                            </div>
                            <div>
                              <p className="text-base font-bold text-gray-900">
                                {category.product_count || 0}
                              </p>
                              <p className="text-xs text-gray-600 font-medium">
                                {isActive ? 'Products' : 'No Products'}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Actions - Always Visible */}
                        <div className="mt-4 pt-3 border-t border-gray-200/50 relative z-10">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-1">
                              <div className={`w-1.5 h-1.5 rounded-full ${
                                isActive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                              }`}></div>
                              <span className="text-xs text-gray-600 font-medium">
                                {isActive ? 'In Use' : 'Empty'}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={() => handleViewCategory(category)}
                                className="group/btn relative p-2 text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110" 
                                title="View Details"
                              >
                                <Eye size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                  View
                                </span>
                              </button>
                              <button 
                                onClick={() => handleEditCategory(category)}
                                className="group/btn relative p-2 text-green-600 bg-green-100 hover:bg-green-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110" 
                                title="Edit Category"
                              >
                                <Edit size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                  Edit
                                </span>
                              </button>
                              <button 
                                onClick={() => handleDeleteCategory(category)}
                                className="group/btn relative p-2 text-red-600 bg-red-100 hover:bg-red-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110" 
                                title="Delete Category"
                              >
                                <Trash2 size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                  Delete
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Category</h2>
                <button
                  onClick={handleCloseAddModal}
                  className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>

              <form onSubmit={handleAddCategory} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Main Category *
                  </label>
                  <input
                    type="text"
                    value={addCategoryForm.main_cat}
                    onChange={(e) => handleAddCategoryFormChange('main_cat', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                      formErrors.main_cat ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Cooking Oils, Canned Foods"
                  />
                  {formErrors.main_cat && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.main_cat}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sub Category *
                  </label>
                  <input
                    type="text"
                    value={addCategoryForm.sub_cat}
                    onChange={(e) => handleAddCategoryFormChange('sub_cat', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                      formErrors.sub_cat ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Sunflower Oil, Sardines"
                  />
                  {formErrors.sub_cat && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.sub_cat}</p>
                  )}
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseAddModal}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addCategoryLoading}
                    className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addCategoryLoading ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Add Category
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Category Modal */}
      {showViewModal && selectedCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <Eye className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Category Details</h2>
                    <p className="text-sm text-gray-500">View category information</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseViewModal}
                  className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>

              <div className="space-y-6">
                {/* Category Info */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                      <Folder className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{selectedCategory.main_cat}</h3>
                      <p className="text-sm text-gray-600">{selectedCategory.sub_cat}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{selectedCategory.product_count || 0}</p>
                      <p className="text-xs text-gray-600">Products</p>
                    </div>
                    <div className="text-center">
                      <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                        (selectedCategory.product_count || 0) > 0 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {(selectedCategory.product_count || 0) > 0 ? 'Active' : 'Inactive'}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">Status</p>
                    </div>
                  </div>
                </div>

                {/* Category ID */}
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Category ID</p>
                      <p className="text-xs text-gray-500">Unique identifier</p>
                    </div>
                    <p className="text-sm font-mono text-gray-600">#{selectedCategory.id}</p>
                  </div>
                </div>

                {/* Created Date */}
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Created</p>
                      <p className="text-xs text-gray-500">When this category was added</p>
                    </div>
                    <p className="text-sm text-gray-600">
                      {new Date(selectedCategory.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-6">
                <button
                  onClick={handleCloseViewModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleCloseViewModal();
                    handleEditCategory(selectedCategory);
                  }}
                  className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center gap-2"
                >
                  <Edit size={16} />
                  Edit Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditModal && selectedCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                    <Edit className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Edit Category</h2>
                    <p className="text-sm text-gray-500">Update category information</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseEditModal}
                  className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>

              <form onSubmit={handleUpdateCategory} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Main Category *
                  </label>
                  <input
                    type="text"
                    value={editCategoryForm.main_cat}
                    onChange={(e) => handleEditCategoryFormChange('main_cat', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                      editFormErrors.main_cat ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Cooking Oils, Canned Foods"
                  />
                  {editFormErrors.main_cat && (
                    <p className="text-red-500 text-sm mt-1">{editFormErrors.main_cat}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sub Category *
                  </label>
                  <input
                    type="text"
                    value={editCategoryForm.sub_cat}
                    onChange={(e) => handleEditCategoryFormChange('sub_cat', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                      editFormErrors.sub_cat ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Sunflower Oil, Sardines"
                  />
                  {editFormErrors.sub_cat && (
                    <p className="text-red-500 text-sm mt-1">{editFormErrors.sub_cat}</p>
                  )}
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseEditModal}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editCategoryLoading}
                    className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editCategoryLoading ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        Updating...
                      </>
                    ) : (
                      <>
                        <Edit size={16} />
                        Update Category
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Category Modal */}
      {showDeleteModal && selectedCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                    <Trash2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Delete Category</h2>
                    <p className="text-sm text-gray-500">This action cannot be undone</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseDeleteModal}
                  className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                      <AlertCircle className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-red-800">Warning</p>
                      <p className="text-xs text-red-600">This will permanently delete the category</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{selectedCategory.main_cat}</h3>
                  <p className="text-sm text-gray-600 mb-3">{selectedCategory.sub_cat}</p>
                  <div className="flex items-center space-x-4">
                    <div className="text-center">
                      <p className="text-lg font-bold text-gray-900">{selectedCategory.product_count || 0}</p>
                      <p className="text-xs text-gray-600">Products</p>
                    </div>
                    <div className="text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${
                        (selectedCategory.product_count || 0) > 0 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {(selectedCategory.product_count || 0) > 0 ? 'Active' : 'Inactive'}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">Status</p>
                    </div>
                  </div>
                </div>

                {(selectedCategory.product_count || 0) > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                        <AlertCircle className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-yellow-800">Products Found</p>
                        <p className="text-xs text-yellow-600">This category has {selectedCategory.product_count} products. You may need to reassign them first.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end space-x-3 pt-6">
                <button
                  onClick={handleCloseDeleteModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deleteCategoryLoading}
                  className="px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteCategoryLoading ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete Category
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </main>
    </Layout>
  );
};

export default Categories;
